/* eslint-disable i18next/no-literal-string */
import { HighContrastSelector, IButtonStyles } from '@fluentui/react';
import { Platform } from 'mcsclient/models';
import { isAndroid, isMacOSOrIOS } from 'store';

import { createAppFluentComponentsStyles } from './AppFluentComponentsStyles';
import { createAppFontStyles } from './AppFontStyles';
import { AppSemanticColors } from './AppSemanticColors';
import { createAppTheme } from './AppTheme';
import { FluentEffects, FluentPalette, FluentSemanticColors } from './ThemeTypes';

const highContrastStyles = () => {
  const solidExpanded = {
    color: 'HighlightText !important',
    background: 'Highlight !important',
    borderColor: 'Highlight !important',
  };
  const solidFocused = {
    ...solidExpanded,
    '::after': {
      borderColor: 'HighlightText !important',
    },
  };
  const solidPressed = {
    ...solidExpanded,
  };

  return {
    solidExpanded,
    solidFocused,
    solidPressed,
  };
};

export const CreateCustomTheme = (
  webViewPlatform: Platform | undefined,
  fluentPalette: FluentPalette,
  fluentSemanticColors: FluentSemanticColors,
  appSemanticColors: AppSemanticColors,
  effects: FluentEffects
) =>
  createAppTheme({
    palette: {
      ...fluentPalette,
    },
    semanticColors: {
      ...fluentSemanticColors,
    },
    isInverted: false,
    appColors: {
      ...appSemanticColors,
    },
    ...(isAndroid(webViewPlatform) && { defaultFontStyle: { fontFamily: 'Roboto' } }),
    ...(isMacOSOrIOS(webViewPlatform) && {
      defaultFontStyle: { fontFamily: 'SF Pro Text, -apple-system, BlinkMacSystemFont, sans-serif' },
    }),
    fonts: {
      tiny: { fontSize: '1rem' },
      xSmall: { fontSize: '1rem' },
      small: { fontSize: '1.2rem' },
      smallPlus: { fontSize: '1.2rem' },
      medium: { fontSize: '1.4rem' },
      mediumPlus: { fontSize: '1.6rem' },
      large: { fontSize: '1.8rem' },
      xLarge: { fontSize: '2rem' },
      xxLarge: { fontSize: '2.4rem' },
      mega: { fontSize: '6.8rem' },
    },
    appFonts: createAppFontStyles(),
    effects,
    components: createAppFluentComponentsStyles(
      {
        PrimaryButton: {
          styles: {
            root: {
              boxShadow: effects.elevation4,
              color: fluentSemanticColors.primaryButtonText,
              background: fluentSemanticColors.primaryButtonBackground,
            },
            rootFocused: {
              color: fluentSemanticColors.primaryButtonText,
              border: 'none !important',
              background: fluentSemanticColors.primaryButtonBackground,
              '::after': {
                border: `2px solid ${fluentPalette.white} !important`,
                inset: '2px !important',
              },
              [HighContrastSelector]: {
                ...highContrastStyles().solidFocused,
              },
            },
            rootHovered: {
              boxShadow: effects.elevation8,
              color: fluentSemanticColors.primaryButtonTextHovered,
              background: fluentSemanticColors.primaryButtonBackgroundHovered,
            },
            rootPressed: {
              color: fluentSemanticColors.primaryButtonTextPressed,
              background: fluentSemanticColors.primaryButtonBackgroundPressed,
              [HighContrastSelector]: {
                ...highContrastStyles().solidPressed,
              },
            },
            rootDisabled: {
              boxShadow: 'none',
              color: fluentSemanticColors.primaryButtonTextDisabled,
              background: fluentSemanticColors.primaryButtonBackgroundDisabled,
            },
            // when MenuButton is open, similar to a "Selected" button state
            rootExpanded: {
              color: fluentSemanticColors.primaryButtonText,
              background: fluentPalette.themePrimary,
              border: 'none',
            },
          } as IButtonStyles,
        },
        // aka "Outline" button
        DefaultButton: {
          styles: {
            root: {
              boxShadow: 'none',
              color: fluentSemanticColors.buttonText,
              border: `1px solid ${fluentSemanticColors.buttonText}`,
              background: 'transparent',
            },
            rootFocused: {
              color: fluentSemanticColors.buttonText,
              background: 'transparent',
              borderColor: fluentSemanticColors.buttonText,
              '::after': {
                border: `2px solid ${fluentSemanticColors.buttonText} !important`,
                inset: '0px !important',
              },
            },
            rootHovered: {
              color: fluentSemanticColors.buttonTextHovered,
              border: `1px solid ${fluentSemanticColors.buttonTextHovered}`,
              background: 'transparent',
            },
            rootPressed: {
              color: fluentSemanticColors.buttonTextPressed,
              border: `1px solid ${fluentSemanticColors.buttonTextPressed}`,
              background: 'transparent',
            },
            rootDisabled: {
              color: fluentSemanticColors.buttonTextDisabled,
              border: `1px solid ${fluentSemanticColors.buttonTextDisabled}!important`,
              background: 'transparent',
            },
            // when MenuButton is open, similar to a "Selected" button state
            rootExpanded: {
              color: fluentPalette.themePrimary,
              background: 'transparent',
              border: `1px solid ${appSemanticColors.blue20ToBlueTint10}`,
            },
          } as IButtonStyles,
        },
        // aka "Semi-transparent" button
        PillButton: {
          styles: {
            root: {
              boxShadow: 'none',
              color: fluentSemanticColors.buttonText,
              background: appSemanticColors.white60ToAnchor20,
            },
            rootFocused: {
              color: fluentSemanticColors.buttonText,
              background: appSemanticColors.white60ToAnchor20,
              '::after': {
                border: `2px solid ${fluentSemanticColors.buttonText} !important`,
                inset: '0px !important',
              },
            },
            rootHovered: {
              color: fluentSemanticColors.buttonTextHovered,
              background: appSemanticColors.white60ToAnchor20,
            },
            rootPressed: {
              color: fluentSemanticColors.buttonTextPressed,
              background: appSemanticColors.white60ToAnchor20,
            },
            rootDisabled: {
              color: fluentSemanticColors.buttonTextDisabled,
              background: appSemanticColors.white60ToAnchor20,
            },
            // when MenuButton is open, similar to a "Selected" button state
            rootExpanded: {
              color: fluentPalette.themePrimary,
              background: appSemanticColors.white60ToAnchor20,
              border: 'none',
            },
          } as IButtonStyles,
        },
        // aka "Transparent" or "Ghost" button
        ActionButton: {
          styles: {
            flexContainer: {
              // :last-child not accurate because ms-layer will be inserted after svg
              // if this button is used for showing a contextual menu
              '>.ms-Button-textContainer ~ svg,>.ms-Button-textContainer ~ span > svg': {
                paddingRight: 6,
              },
              '>svg:first-child,>*:first-child svg': {
                paddingLeft: 6,
              },
            },
            root: {
              boxShadow: 'none',
              color: appSemanticColors.actionButtonText,
              background: 'transparent',
            },
            rootFocused: {
              color: appSemanticColors.actionButtonText,
              background: 'transparent',
              '::after': {
                border: `2px solid ${appSemanticColors.actionButtonText} !important`,
                inset: '0 !important',
              },
            },
            rootHovered: {
              color: fluentSemanticColors.buttonTextHovered,
              background: 'transparent',
            },
            rootPressed: {
              color: fluentSemanticColors.buttonTextPressed,
              background: 'transparent',
            },
            rootDisabled: {
              color: fluentSemanticColors.buttonTextDisabled,
              background: 'transparent',
            },
            // when MenuButton is open, similar to a "Selected" button state
            rootExpanded: {
              color: fluentPalette.themePrimary,
              background: 'transparent',
              border: 'none',
            },
          } as IButtonStyles,
        },
        // aka red "Delete" or "Remove" button
        // Note: use the same background color for both themes
        DestructiveButton: {
          styles: {
            root: {
              boxShadow: effects.elevation4,
              color: fluentSemanticColors.primaryButtonText,
              background: appSemanticColors.criticalRed20,
            },
            rootFocused: {
              color: fluentSemanticColors.primaryButtonText,
              border: 'none !important',
              background: appSemanticColors.criticalRed20,
              '::after': {
                border: `2px solid ${fluentPalette.white} !important`,
                inset: '2px !important',
              },
              [HighContrastSelector]: {
                ...highContrastStyles().solidFocused,
              },
            },
            rootHovered: {
              boxShadow: effects.elevation8,
              color: fluentSemanticColors.primaryButtonTextHovered,
              background: appSemanticColors.criticalRed10,
            },
            rootPressed: {
              color: fluentSemanticColors.primaryButtonTextPressed,
              background: appSemanticColors.criticalRed30,
              borderColor: appSemanticColors.criticalRed30,
              [HighContrastSelector]: {
                ...highContrastStyles().solidPressed,
              },
            },
            rootDisabled: {
              boxShadow: 'none',
              color: fluentSemanticColors.primaryButtonTextDisabled,
              background: fluentSemanticColors.primaryButtonBackgroundDisabled,
            },
            // when MenuButton is open, similar to a "Selected" button state
            rootExpanded: {
              color: fluentSemanticColors.primaryButtonText,
              background: appSemanticColors.criticalPrimary,
              border: 'none',
              [HighContrastSelector]: {
                ...highContrastStyles().solidExpanded,
              },
            },
          } as IButtonStyles,
        },
      },
      fluentPalette,
      appSemanticColors
    ),
  });
